import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTimePicker } from '@material-ui/pickers';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import TextField from '@material-ui/core/TextField';
import Switch from '@material-ui/core/Switch';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';


const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        margin="normal"
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    section: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    formContainer: {
        overflowY: 'scroll',
        height: '100%',
        padding: theme.spacing(3)
    },
    settingsContainer: {
        height: '100%',
        padding: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    image: {
        maxHeight: 300,
        maxWidth: 300,
        height: 'auto',
        width: '100%'
    },
    imageContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        padding: theme.spacing(6)
    },
    height: {
        height: '100%'
    }
}));

export default function RecommendationDetails(props) {

    const {
        election,
        handleElection,
        newElection,
        isError,
        getErrorMessage,
        errors,
        groups
    } = props;

    const classes = useStyles();

    const recommendationsTextMessage = useMemo(() => {
        if (isError('recommendations_text')) return errors['recommendations_text'];
        return "Leave empty for the default header in the recommendation dialogue."
    }, [errors]);

    const recommendationsStartDateMessage = useMemo(() => {
        if (isError('recommendations_start_at')) return errors['recommendations_start_at'];
        return "A blank value will allow recommendations to be made at anytime during the election cycle."
    }, [errors]);

    const recommendationsPerUserMessage = useMemo(() => {
        if (isError('recommendations_per_user')) return errors['recommendations_per_user'];
        return "0 will allow no limit on recommendations"
    }, [errors]);

    const recommendationsRepeatLengthMessage = useMemo(() => {
        if (isError('recommendations_repeat_length')) return errors['recommendations_repeat_length'];
        return "0 will keep a recommendation as long as its proposal exists"
    }, [errors]);

    const recommendationsCharacterLimitMessage = useMemo(() => {
        if (isError('recommendations_character_limit')) return errors['recommendations_character_limit'];
        return "Must be greater than 50 and less than 10,000"
    }, [errors]);

    const initialStartDate = useMemo(() => {
        if (election.proposals_start_at) return election.proposals_start_at;
        return election.starts_at;

    }, [election.starts_at, election.proposals_start_at]);

    const initialEndDate = useMemo(() => {

        const ied = election.starts_at.plus({[election.repeat_period]: election.repeat_amount})
        const nied = initialStartDate.plus({weeks: 2}).startOf('hour');

        return (ied > nied) ? nied : ied;

    }, [initialStartDate]);

    return (
        <Grid container direction="row" className={newElection ? classes.height : null}>
            {newElection &&
                <Grid item xs className={classes.height}>
                    <div className={classes.imageContainer}>
                        <img className={classes.image} src='/images/step-recommendation.png' alt="rec-graphic" />
                    </div>
                </Grid>
            }
            <Grid item xs={newElection ? 7 : 12} className={newElection ? classes.formContainer : classes.settingsContainer}>
                {newElection &&
                    <Typography variant='h6' gutterBottom>
                        Recommendations
                    </Typography>
                }
                <FormControl>
                        <FormControlLabel
                            margin="normal"
                            control={
                                <Switch
                                    checked={election.recommendations_active}
                                    onChange={handleElection('recommendations_active')}
                                    color="primary"
                                    value="recommendations_active"
                                />
                            }
                            label="Allow users to create recommendations?"
                            labelPlacement="end"
                        />
                        <FormHelperText>
                            When disabled, administrators will still be able to create recommendations for proposals
                        </FormHelperText>
                    </FormControl>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        Select the group of users that can create recommendations.
                    </Typography>
                    <TextField
                        id="input-recommendation-group-id"
                        select
                        value={election.recommendation_group_id}
                        onChange={handleElection('recommendation_group_id')}
                        variant="outlined"
                        fullWidth
                        error={isError('recommendation_group_id')}
                        helperText={getErrorMessage('recommendation_group_id')}
                        disabled={! election.recommendations_active}
                        >
                        <MenuItem value={0}>
                            All Users
                        </MenuItem>
                        {groups.map(group => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.title}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        What date and time should users be allowed to start making recommendations?
                    </Typography>
                    <DateTimePicker
                        clearable
                        label="Recommendations Start Date"
                        inputVariant="outlined"
                        value={election.recommendations_start_at}
                        onChange={handleElection('recommendations_start_at')}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        disabled={! election.recommendations_active}
                        helperText={recommendationsStartDateMessage}
                        initialFocusedDate={initialStartDate}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        What date and time should recommendations close?
                    </Typography>
                    <DateTimePicker
                        clearable
                        label="Recommendations End Date"
                        inputVariant="outlined"
                        value={election.recommendations_end_at}
                        onChange={handleElection('recommendations_end_at')}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        minutesStep={15}
                        disabled={! election.recommendations_active}
                        initialFocusedDate={initialEndDate}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        How many recommendations can a user make?
                    </Typography>
                    <TextField
                        id="input-recommendations-per-user"
                        value={election.recommendations_per_user}
                        onChange={handleElection('recommendations_per_user')}
                        fullWidth
                        type="number"
                        error={isError('recommendations_per_user')}
                        helperText={recommendationsPerUserMessage}
                        variant="outlined"
                        disabled={! election.recommendations_active}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        How many characters can a user write for each recommendation?
                    </Typography>
                    <TextField
                        id="input-recommendations-character-limit"
                        value={election.recommendations_character_limit}
                        onChange={handleElection('recommendations_character_limit')}
                        fullWidth
                        type="number"
                        error={isError('recommendations_character_limit')}
                        helperText={recommendationsCharacterLimitMessage}
                        variant="outlined"
                        disabled={! election.recommendations_active}
                    />
                </div>
                <div className={classes.section}>
                    <FormControlLabel
                        margin="normal"
                        control={
                            <Switch
                                checked={election.recommendations_canned_responses}
                                onChange={handleElection('recommendations_canned_responses')}
                                color="primary"
                                value="recommendations_canned_responses"
                            />
                        }
                        label="Can users use recommendation templates for this election?"
                        labelPlacement="end"
                        disabled={! election.recommendations_active}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        Customize the header text in the recommendation dialogue.
                    </Typography>
                    <TextField
                        id="input-recommendations-text"
                        name="recommendations_text"
                        value={election.recommendations_text}
                        onChange={handleElection('recommendations_text')}
                        fullWidth
                        error={isError('recommendations_text')}
                        helperText={recommendationsTextMessage}
                        variant="outlined"
                        multiline
                        rows="4"
                        rowsMax="4"
                        disabled={! election.recommendations_active}
                        inputProps={{
                            maxLength: "255"
                        }}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        Customize the "All Proposals" label on the recommendations screen.
                    </Typography>
                    <TextField
                        id="input-recommendations-all-label"
                        name="recommendations_all_label"
                        value={election.recommendations_all_label}
                        onChange={handleElection('recommendations_all_label')}
                        fullWidth
                        error={isError('recommendations_all_label')}
                        helperText={getErrorMessage('recommendations_all_label')}
                        variant="outlined"
                        disabled={! election.recommendations_active}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        Customize the "Submitted Proposals" label on the recommendations screen.
                    </Typography>
                    <TextField
                        id="input-recommendations-submitted-label"
                        name="recommendations_submitted_label"
                        value={election.recommendations_submitted_label}
                        onChange={handleElection('recommendations_submitted_label')}
                        fullWidth
                        error={isError('recommendations_submitted_label')}
                        helperText={getErrorMessage('recommendations_submitted_label')}
                        variant="outlined"
                        disabled={! election.recommendations_active}
                    />
                </div>
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        Customize the "Related Proposals" label on the recommendations screen.
                    </Typography>
                    <TextField
                        id="input-recommendations-related-label"
                        name="recommendations_related_label"
                        value={election.recommendations_related_label}
                        onChange={handleElection('recommendations_related_label')}
                        fullWidth
                        error={isError('recommendations_related_label')}
                        helperText={getErrorMessage('recommendations_related_label')}
                        variant="outlined"
                        disabled={! election.recommendations_active}
                    />
                </div>
                <div className={classes.section}>
                    <FormControlLabel
                        margin="normal"
                        control={
                            <Switch
                                checked={election.recommendations_starting_notifications}
                                onChange={handleElection('recommendations_starting_notifications')}
                                color="primary"
                                value="recommendations_starting_notifications"
                            />
                        }
                        label="Should Recommendation Group Members be sent a notification once they can make recommendations?"
                        labelPlacement="end"
                        disabled={(!election.recommendations_active)}
                    />
                    <FormControlLabel
                        margin="normal"
                        control={
                            <Switch
                                checked={election.recommendations_ending_notifications}
                                onChange={handleElection('recommendations_ending_notifications')}
                                color="primary"
                                value="recommendations_ending_notifications"
                            />
                        }
                        label="Should Recommendation Group Members receive a warning before recommendations close?"
                        labelPlacement="end"
                        disabled={(!election.recommendations_active)}
                    />
                </div>
                <Divider className={classes.section}/>
                <FormControlLabel
                    margin="normal"
                    control={
                        <Switch
                            checked={election.recommendations_repeated}
                            onChange={handleElection('recommendations_repeated')}
                            color="primary"
                            value="recommendations_repeated"
                        />
                    }
                    label="Should recommendations to be passed to next election cycle?"
                    labelPlacement="end"
                    disabled={(!election.repeated || !election.proposals_repeated)}
                />
                <div className={classes.section}>
                    <Typography variant='subtitle2' gutterBottom>
                        How many election cycles should a recommendation be kept for?
                    </Typography>
                    <TextField
                        id="input-recommendations-repeat-length"
                        value={election.recommendations_repeat_length}
                        max={election.recommendations_repeat_length}
                        onChange={handleElection('recommendations_repeat_length')}
                        fullWidth
                        type="number"
                        error={isError('recommendations_repeat_length')}
                        helperText={recommendationsRepeatLengthMessage}
                        variant="outlined"
                        disabled={!election.recommendations_repeated}
                    />
                </div>
            </Grid>
        </Grid>
    )
}
